/**
 * Numeric eigenvalues and eigenvectors for matrices the exact route cannot finish.
 *
 * Reduce to Hessenberg form with Givens rotations, then run the shifted QR algorithm in
 * complex arithmetic (so a conjugate pair needs no special double-shift step), splitting
 * the matrix wherever a subdiagonal entry becomes negligible. Eigenvectors come from
 * inverse iteration on (A − λI). Double precision: expect roughly ten correct digits for
 * a well-conditioned matrix, fewer for a defective one.
 */

export interface Complex {
  re: number;
  im: number;
}

export type RealMatrix = number[][];

/** Eigenvalues with an imaginary part below this fraction of their size count as real. */
export const REAL_TOLERANCE = 1e-9;
const DEFLATION = 1e-14;
const MAX_ITERATIONS = 500;

const cx = (re: number, im = 0): Complex => ({ re, im });
const add = (x: Complex, y: Complex): Complex => cx(x.re + y.re, x.im + y.im);
const sub = (x: Complex, y: Complex): Complex => cx(x.re - y.re, x.im - y.im);
const mul = (x: Complex, y: Complex): Complex =>
  cx(x.re * y.re - x.im * y.im, x.re * y.im + x.im * y.re);
const conj = (x: Complex): Complex => cx(x.re, -x.im);
const abs = (x: Complex): number => Math.hypot(x.re, x.im);
const scale = (x: Complex, k: number): Complex => cx(x.re * k, x.im * k);

function div(x: Complex, y: Complex): Complex {
  const d = y.re * y.re + y.im * y.im;
  return cx((x.re * y.re + x.im * y.im) / d, (x.im * y.re - x.re * y.im) / d);
}

function sqrtC(x: Complex): Complex {
  const r = abs(x);
  const re = Math.sqrt((r + x.re) / 2);
  const im = Math.sqrt(Math.max(0, (r - x.re) / 2));
  return cx(re, x.im < 0 ? -im : im);
}

/* -------------------------------------------------------------- Hessenberg */

/** Similarity transform to upper Hessenberg form by Givens rotations (same eigenvalues). */
function hessenberg(input: RealMatrix): RealMatrix {
  const n = input.length;
  const h = input.map((row) => row.slice());
  for (let j = 0; j < n - 2; j += 1) {
    for (let i = n - 1; i > j + 1; i -= 1) {
      const a = h[i - 1][j];
      const b = h[i][j];
      if (b === 0) {
        continue;
      }
      const r = Math.hypot(a, b);
      const c = a / r;
      const s = b / r;
      for (let k = 0; k < n; k += 1) {
        const u = h[i - 1][k];
        const v = h[i][k];
        h[i - 1][k] = c * u + s * v;
        h[i][k] = -s * u + c * v;
      }
      for (let k = 0; k < n; k += 1) {
        const u = h[k][i - 1];
        const v = h[k][i];
        h[k][i - 1] = c * u + s * v;
        h[k][i] = -s * u + c * v;
      }
    }
  }
  return h;
}

/* -------------------------------------------------------------------- QR */

type ComplexMatrix = Complex[][];

function wilkinsonShift(h: ComplexMatrix): Complex {
  const m = h.length;
  const a = h[m - 2][m - 2];
  const b = h[m - 2][m - 1];
  const c = h[m - 1][m - 2];
  const d = h[m - 1][m - 1];
  const half = scale(sub(a, d), 0.5);
  const disc = sqrtC(add(mul(half, half), mul(b, c)));
  const first = add(d, add(half, disc));
  const second = add(d, sub(half, disc));
  // Of the two eigenvalues of the trailing 2×2 block, the one nearer h[m-1][m-1].
  return abs(sub(first, d)) < abs(sub(second, d)) ? first : second;
}

/** One shifted QR step on a Hessenberg block, done in place with Givens rotations. */
function qrStep(h: ComplexMatrix, shift: Complex): void {
  const m = h.length;
  for (let i = 0; i < m; i += 1) {
    h[i][i] = sub(h[i][i], shift);
  }
  const rotations: { a: Complex; b: Complex; r: number }[] = [];
  for (let k = 0; k < m - 1; k += 1) {
    const a = h[k][k];
    const b = h[k + 1][k];
    const r = Math.hypot(abs(a), abs(b));
    rotations.push({ a, b, r });
    if (r === 0) {
      continue;
    }
    for (let j = k; j < m; j += 1) {
      const u = h[k][j];
      const v = h[k + 1][j];
      h[k][j] = scale(add(mul(conj(a), u), mul(conj(b), v)), 1 / r);
      h[k + 1][j] = scale(add(mul(scale(b, -1), u), mul(a, v)), 1 / r);
    }
  }
  for (let k = 0; k < m - 1; k += 1) {
    const { a, b, r } = rotations[k];
    if (r === 0) {
      continue;
    }
    for (let i = 0; i <= Math.min(k + 2, m - 1); i += 1) {
      const u = h[i][k];
      const v = h[i][k + 1];
      h[i][k] = scale(add(mul(u, a), mul(v, b)), 1 / r);
      h[i][k + 1] = scale(
        add(mul(scale(u, -1), conj(b)), mul(v, conj(a))),
        1 / r
      );
    }
  }
  for (let i = 0; i < m; i += 1) {
    h[i][i] = add(h[i][i], shift);
  }
}

function negligible(h: ComplexMatrix, k: number): boolean {
  const size = abs(h[k][k]) + abs(h[k - 1][k - 1]);
  return abs(h[k][k - 1]) <= DEFLATION * (size === 0 ? 1 : size);
}

function blockEigenvalues(h: ComplexMatrix, out: Complex[]): void {
  const m = h.length;
  if (m === 1) {
    out.push(h[0][0]);
    return;
  }
  for (let iteration = 0; iteration < MAX_ITERATIONS; iteration += 1) {
    for (let k = m - 1; k >= 1; k -= 1) {
      if (negligible(h, k)) {
        const top = h.slice(0, k).map((row) => row.slice(0, k));
        const bottom = h.slice(k).map((row) => row.slice(k));
        blockEigenvalues(top, out);
        blockEigenvalues(bottom, out);
        return;
      }
    }
    // An exceptional shift every ten steps breaks any stalled symmetric pattern.
    const shift =
      iteration % 10 === 9
        ? add(h[m - 1][m - 1], cx(abs(h[m - 1][m - 2]), 0))
        : wilkinsonShift(h);
    qrStep(h, shift);
  }
  // Out of iterations: take the diagonal as the best available estimate.
  for (let i = 0; i < m; i += 1) {
    out.push(h[i][i]);
  }
}

/** Every eigenvalue of a real square matrix, conjugate pairs made exact conjugates. */
export function numericEigenvalues(a: RealMatrix): Complex[] {
  const h = hessenberg(a).map((row) => row.map((value) => cx(value)));
  const found: Complex[] = [];
  blockEigenvalues(h, found);
  const norm = Math.max(1, ...a.flat().map((x) => Math.abs(x)));
  const cleaned = found.map((value) =>
    Math.abs(value.im) <= REAL_TOLERANCE * Math.max(norm, abs(value))
      ? cx(value.re, 0)
      : value
  );
  return pairConjugates(cleaned).sort(byValue);
}

function pairConjugates(values: Complex[]): Complex[] {
  const result = values.map((v) => ({ ...v }));
  for (let i = 0; i < result.length; i += 1) {
    if (result[i].im <= 0) {
      continue;
    }
    let best = -1;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let j = 0; j < result.length; j += 1) {
      if (j === i || result[j].im >= 0) {
        continue;
      }
      const distance = abs(sub(result[j], conj(result[i])));
      if (distance < bestDistance) {
        bestDistance = distance;
        best = j;
      }
    }
    if (best >= 0) {
      const re = (result[i].re + result[best].re) / 2;
      const im = (result[i].im - result[best].im) / 2;
      result[i] = cx(re, im);
      result[best] = cx(re, -im);
    }
  }
  return result;
}

function byValue(x: Complex, y: Complex): number {
  if (Math.abs(x.re - y.re) > 1e-9) {
    return y.re - x.re;
  }
  return y.im - x.im;
}

/* ------------------------------------------------------- inverse iteration */

function solveComplex(m: ComplexMatrix, rhs: Complex[]): Complex[] | null {
  const n = m.length;
  const a = m.map((row, i) => [...row.map((v) => ({ ...v })), { ...rhs[i] }]);
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let r = col + 1; r < n; r += 1) {
      if (abs(a[r][col]) > abs(a[pivot][col])) {
        pivot = r;
      }
    }
    if (abs(a[pivot][col]) === 0) {
      return null;
    }
    [a[col], a[pivot]] = [a[pivot], a[col]];
    for (let r = col + 1; r < n; r += 1) {
      const factor = div(a[r][col], a[col][col]);
      for (let c = col; c <= n; c += 1) {
        a[r][c] = sub(a[r][c], mul(factor, a[col][c]));
      }
    }
  }
  const x: Complex[] = Array.from({ length: n }, () => cx(0));
  for (let r = n - 1; r >= 0; r -= 1) {
    let sum = a[r][n];
    for (let c = r + 1; c < n; c += 1) {
      sum = sub(sum, mul(a[r][c], x[c]));
    }
    x[r] = div(sum, a[r][r]);
  }
  return x;
}

function normalizeVector(v: Complex[]): Complex[] {
  let index = 0;
  for (let i = 1; i < v.length; i += 1) {
    if (abs(v[i]) > abs(v[index]) * (1 + 1e-9)) {
      index = i;
    }
  }
  const pivot = v[index];
  if (abs(pivot) === 0) {
    return v;
  }
  return v.map((value, i) => (i === index ? cx(1, 0) : div(value, pivot)));
}

function project(v: Complex[], onto: Complex[]): Complex[] {
  // v − (⟨onto, v⟩ / ⟨onto, onto⟩) onto, with ⟨x, y⟩ = Σ conj(x)·y.
  let top = cx(0);
  let bottom = 0;
  for (let i = 0; i < v.length; i += 1) {
    top = add(top, mul(conj(onto[i]), v[i]));
    bottom += abs(onto[i]) ** 2;
  }
  const factor = scale(top, 1 / bottom);
  return v.map((value, i) => sub(value, mul(factor, onto[i])));
}

/**
 * An eigenvector for λ by inverse iteration. `earlier` holds vectors already found for
 * the same (repeated) eigenvalue; each new one is kept orthogonal to them so a repeated
 * eigenvalue of a symmetric matrix yields a full set rather than one vector n times.
 */
export function numericEigenvector(
  a: RealMatrix,
  lambda: Complex,
  earlier: Complex[][],
  seed: number
): Complex[] {
  const n = a.length;
  const norm = Math.max(1, ...a.flat().map((x) => Math.abs(x)));
  const nudge = cx(lambda.re + norm * 1e-10, lambda.im);
  const shifted: ComplexMatrix = a.map((row, i) =>
    row.map((value, j) => (i === j ? sub(cx(value), nudge) : cx(value)))
  );
  let v: Complex[] = Array.from({ length: n }, (_, i) =>
    cx(1 + ((i * (seed + 2) + seed) % 5) / 4)
  );
  for (let step = 0; step < 6; step += 1) {
    for (const previous of earlier) {
      v = project(v, previous);
    }
    const solved = solveComplex(shifted, v);
    if (solved === null) {
      break;
    }
    const size = Math.sqrt(solved.reduce((sum, x) => sum + abs(x) ** 2, 0));
    v = solved.map((x) => scale(x, 1 / size));
  }
  return normalizeVector(v);
}
